import React from 'react'
import { createPortal } from 'react-dom'
import { useApp } from '../context/AppContext'
import { api } from '../api/client'
import { screenCache } from '../api/screenCache'
import type { ClientListItem, ClientPage } from '../types/app'
import { displayName, isCurrentRequest, visitSummary } from '../clients/list'
import { saveErrorMessage, showToast } from './settings/toast'
import NewClientForm from '../components/NewClientForm'
import { useDialog } from '../hooks/useDialog'
import '../components/Sheet.css'
import './settings/Settings.css'
import './Clients.css'
import { Loading, EmptyState } from '../ui/States'

// AddClientSheet adds a client from the Clients tab, without booking them.
function AddClientSheet({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string, firstName: string) => void }) {
  const [busy, setBusy] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)
  useDialog(ref, onClose, { closable: !busy })
  // Rendered into the app shell, above the tab bar (see ExpenseSheet).
  const host = typeof document === 'undefined' ? null : document.querySelector('.app-container')
  const sheet = (
    <div className="sheet-overlay" onClick={() => { if (!busy) onClose() }}>
      <div ref={ref} className="sheet-content" role="dialog" aria-modal="true" aria-labelledby="add-client-title" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle"></div>
        <div className="sheet-header">
          <h2 id="add-client-title">Add client</h2>
        </div>
        <div className="sheet-body">
          <NewClientForm
            cancelLabel="Cancel"
            onBusyChange={setBusy}
            onCancel={onClose}
            onCreated={(c) => onCreated(c.id, c.first_name)}
          />
        </div>
      </div>
    </div>
  )
  return host ? createPortal(sheet, host) : sheet
}

type Filter = '' | 'new' | 'vip' | 'no_email'

const FILTERS: { value: Filter; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'vip', label: 'VIP' },
  { value: 'no_email', label: 'No email' },
]

// Only the first page is cached, keyed by the query and filter it was
// fetched for: a later page loaded with "Load more" is never shown on its
// own, so it isn't worth caching.
function clientsCacheKey(filter: Filter, query: string): string {
  return `clients:${filter}:${query}`
}

export default function Clients() {
  const { state, dispatch } = useApp()
  const [filter, setFilter] = React.useState<Filter>('')
  const [debouncedQuery, setDebouncedQuery] = React.useState(state.query.trim())
  const [clients, setClients] = React.useState<ClientListItem[] | null>(
    () => screenCache.get<ClientPage>(clientsCacheKey(filter, debouncedQuery))?.clients ?? null,
  )
  const [nextCursor, setNextCursor] = React.useState<string | null>(
    () => screenCache.get<ClientPage>(clientsCacheKey(filter, debouncedQuery))?.next_cursor ?? null,
  )
  const [failed, setFailed] = React.useState(false)
  const [loadingMore, setLoadingMore] = React.useState(false)
  const [adding, setAdding] = React.useState(false)

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(state.query.trim()), 300)
    return () => clearTimeout(timer)
  }, [state.query])

  const latest = React.useRef(0)

  // A cached first page for this query and filter is shown at once, and a
  // failed background refresh just keeps it instead of the error screen.
  const load = React.useCallback(() => {
    const request = ++latest.current
    const key = clientsCacheKey(filter, debouncedQuery)
    const cached = screenCache.get<ClientPage>(key)
    setClients(cached?.clients ?? null)
    setNextCursor(cached?.next_cursor ?? null)
    setFailed(false)
    api
      .listClients({ q: debouncedQuery, filter })
      .then((page) => {
        if (isCurrentRequest(request, latest.current)) {
          setClients(page.clients)
          setNextCursor(page.next_cursor)
          screenCache.set(key, page)
        }
      })
      .catch(() => {
        if (isCurrentRequest(request, latest.current) && !cached) {
          setFailed(true)
        }
      })
  }, [debouncedQuery, filter])

  React.useEffect(() => {
    load()
  }, [load])

  const loadMore = async () => {
    if (!nextCursor) return
    // Take a token from the same counter `load()` uses: if the filter or
    // search changes while this request is in flight, `load()` bumps the
    // counter and this response — for the old filter — is dropped instead of
    // being appended to the newly-loaded list.
    const request = ++latest.current
    setLoadingMore(true)
    try {
      const page = await api.listClients({ q: debouncedQuery, filter, cursor: nextCursor })
      if (isCurrentRequest(request, latest.current)) {
        setClients((prev) => [...(prev ?? []), ...page.clients])
        setNextCursor(page.next_cursor)
      }
    } catch (e) {
      if (isCurrentRequest(request, latest.current)) {
        showToast(dispatch, saveErrorMessage(e))
      }
    } finally {
      setLoadingMore(false)
    }
  }

  const searching = debouncedQuery !== '' || filter !== ''

  return (
    <div className="screen clients-screen">
      <div className="clients-head">
        <h1>Clients</h1>
        <button type="button" className="btn btn-secondary btn-compact" onClick={() => setAdding(true)}>
          Add client
        </button>
      </div>

      <input
        type="search"
        aria-label="Search clients"
        placeholder="Search name or phone"
        value={state.query}
        onChange={(e) => dispatch({ type: 'SET_QUERY', payload: e.target.value })}
        className="search-input"
      />

      <div className="clients-filters" role="group" aria-label="Filter">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className="chip"
            aria-pressed={filter === f.value}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {clients === null ? (
        failed ? (
          <div className="clients-status" role="alert">
            <p>Couldn’t load your clients. Check your connection and try again.</p>
            <button type="button" className="btn btn-secondary btn-compact" onClick={load}>
              Try again
            </button>
          </div>
        ) : (
          <Loading />
        )
      ) : clients.length === 0 ? (
        searching ? (
          <div className="clients-empty">No clients match that search.</div>
        ) : (
          <EmptyState
            title="No clients yet"
            action={
              <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
                Add client
              </button>
            }
          >
            Add the people you already see, or they’ll appear here as you book them.
          </EmptyState>
        )
      ) : (
        <>
          <ul className="clients-list" aria-label="Clients">
            {clients.map((client) => (
              <li key={client.id}>
                <button
                  type="button"
                  className="client-card"
                  onClick={() => dispatch({ type: 'OPEN_CLIENT', payload: client.id })}
                >
                  <span className="client-row-top">
                    <span className="client-name">{displayName(client)}</span>
                    {client.vip && <span className="tag tag--primary">VIP</span>}
                  </span>
                  <span className="client-meta">{visitSummary(client)}</span>
                </button>
              </li>
            ))}
          </ul>
          {nextCursor && (
            <button type="button" className="btn btn-quiet btn-block" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </>
      )}
      {adding && (
        <AddClientSheet
          onClose={() => setAdding(false)}
          onCreated={(id, firstName) => {
            setAdding(false)
            screenCache.clearAll()
            showToast(dispatch, `${firstName} added`)
            dispatch({ type: 'OPEN_CLIENT', payload: id })
          }}
        />
      )}
    </div>
  )
}
